/**
 * Money is stored and computed as integer minor units. Never floats.
 * Decimal strings are parsed digit-wise to avoid binary-float drift.
 */
const CENTS_PER_UNIT = 100;
const QTY_SCALE = 10000n;
const QTY_ROUND_HALF = QTY_SCALE / 2n;
const DECIMAL_2 = /^-?\d{1,12}(\.\d{1,2})?$/;
const DECIMAL_4 = /^\d{1,9}(\.\d{1,4})?$/;
export function parseMoney(input) {
    const trimmed = input.trim();
    if (!DECIMAL_2.test(trimmed)) {
        return { ok: false, message: "Must be a number with at most 2 decimal places." };
    }
    const negative = trimmed.startsWith("-");
    const digits = negative ? trimmed.slice(1) : trimmed;
    const [whole = "0", frac = ""] = digits.split(".");
    const fracPadded = (frac + "00").slice(0, 2);
    const value = Number(whole) * CENTS_PER_UNIT + Number(fracPadded);
    if (!Number.isSafeInteger(value)) {
        return { ok: false, message: "Amount is out of range." };
    }
    return { ok: true, minorUnits: negative ? -value : value };
}
export function addMoney(...amounts) {
    return amounts.reduce((sum, a) => sum + a, 0);
}
export function sumMoney(amounts) {
    return amounts.reduce((sum, a) => sum + a, 0);
}
function decimalToScaled4(input) {
    const trimmed = input.trim();
    if (!DECIMAL_4.test(trimmed))
        return null;
    const [whole = "0", frac = ""] = trimmed.split(".");
    const fracPadded = (frac + "0000").slice(0, 4);
    return BigInt(whole) * QTY_SCALE + BigInt(fracPadded);
}
/**
 * Multiply a minor-unit price by a decimal quantity (max 4 dp),
 * rounding half-up to the nearest minor unit.
 */
export function multiplyMoney(minorUnits, quantity) {
    if (!Number.isSafeInteger(minorUnits)) {
        return { ok: false, message: "Price is not a valid minor-unit integer." };
    }
    const qty = decimalToScaled4(quantity);
    if (qty === null) {
        return {
            ok: false,
            message: "Quantity must be a positive number with at most 4 decimal places.",
        };
    }
    const product = BigInt(minorUnits) * qty;
    const sign = product < 0n ? -1n : 1n;
    const abs = product < 0n ? -product : product;
    const rounded = (abs + QTY_ROUND_HALF) / QTY_SCALE;
    const value = Number(sign * rounded);
    if (!Number.isSafeInteger(value)) {
        return { ok: false, message: "Line total is out of range." };
    }
    return { ok: true, minorUnits: value };
}
export function isValidQuantity(quantity) {
    return decimalToScaled4(quantity) !== null && decimalToScaled4(quantity) > 0n;
}
export function formatMoney(minorUnits, currency, locale = "en") {
    const formatter = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2, // display always shows cents precision
    });
    return formatter.format(minorUnits / 100);
}
const AMOUNT_FORMATTER = new Intl.NumberFormat("en", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
/** Formats minor units as a plain, currency-less amount (e.g. "1,234.56"). */
export function formatAmount(minorUnits) {
    return AMOUNT_FORMATTER.format(minorUnits / 100);
}
//# sourceMappingURL=money.js.map